import { decodeKeyMarkers, encodeKeyMarker } from './marker';

/**
 * Returns true when the build was made with `I18N_CAPTURE=true` (never in production builds).
 * @returns {boolean} whether capture mode is on.
 */
export const isCaptureModeEnabled = () => (
  typeof I18N_CAPTURE !== 'undefined' && I18N_CAPTURE === true
);

// Calculation inputs parsed as numbers (for example `math.energy_price.*`); a marker corrupts them.
const CALCULATION_KEY_PREFIX = 'math.';

// Link destinations (for example `footer.navigation.text0.url`) are used as `href` values, which a
// marker would corrupt.
const URL_KEY_SUFFIX = '.url';
const URL_MESSAGE_PATTERN = /^\s*(https?:|mailto:|tel:)/i;

const isUrlMessage = (key, message) => (
  key.endsWith(URL_KEY_SUFFIX) || URL_MESSAGE_PATTERN.test(message)
);

const isMarkable = (key, message) => (
  typeof message === 'string'
  && message !== key
  && !key.startsWith(CALCULATION_KEY_PREFIX)
  && !isUrlMessage(key, message)
);

/**
 * Appends a key marker to every message, except messages equal to their own key
 * (the fallback-to-id convention used by `nullable.js`), calculation messages and link
 * destinations.
 * @param {object} messages - compiled messages keyed by id.
 * @returns {object} the wrapped messages.
 */
export const wrapMessagesWithMarkers = (messages) => {
  if (!messages || typeof messages !== 'object') {
    return messages;
  }

  return Object.fromEntries(Object.entries(messages).map(([key, message]) => (
    [key, isMarkable(key, message) ? `${message}${encodeKeyMarker(key)}` : message]
  )));
};

// Bootstrap's skeleton bar. A loading skeleton is hidden from assistive technology but still paints
// its real breadcrumbs, headings and column names beside these bars, so that text is on screen.
const SKELETON_BAR_SELECTOR = '.placeholder';

const isSkeleton = (element) => Boolean(element.querySelector(SKELETON_BAR_SELECTOR));

/**
 * Returns true when the element or one of its ancestors is hidden. An `aria-hidden` loading
 * skeleton is not hidden from view, but the text inside one of its bars is unreadable.
 * @param {Element} element - the element to check.
 * @returns {boolean} whether the element is hidden.
 */
const isElementHidden = (element) => {
  if (element.closest(SKELETON_BAR_SELECTOR)) {
    return true;
  }
  for (let current = element; current; current = current.parentElement) {
    if (current.getAttribute('aria-hidden') === 'true' && !isSkeleton(current)) {
      return true;
    }
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return true;
    }
  }
  return false;
};

/**
 * Returns true when another element, such as a modal backdrop or dropdown, covers the centre of
 * the rect. Points outside the viewport cannot be checked, so they are treated as uncovered; the
 * capture runner scrolls the element into view and reads the keys again.
 * @param {Element} element - the element that shows the text.
 * @param {{x: number, y: number, width: number, height: number}} rect - the element's rect.
 * @returns {boolean} whether the element is covered.
 */
const isElementCovered = (element, rect) => {
  // Browsers do not hit-test content that overflows an SVG foreignObject, such as a chart tooltip
  // drawn beside its one-pixel host, so check the host's own box. Anything inside the same chart
  // is part of the tooltip's own drawing; an overlay such as a modal still covers it.
  const host = element.closest('foreignObject');
  const box = host ? host.getBoundingClientRect() : rect;
  const owner = host ? host.ownerSVGElement || host : element;
  const centreX = box.x + box.width / 2;
  const centreY = box.y + box.height / 2;
  if (centreX < 0 || centreY < 0 || centreX >= window.innerWidth || centreY >= window.innerHeight) {
    return false;
  }
  const top = document.elementFromPoint(centreX, centreY);
  return Boolean(top) && !owner.contains(top) && !top.contains(owner);
};

/**
 * Returns the visible bounding rect of an element, or null when it is hidden, has no size or is
 * covered by another element.
 * @param {Element} element - the element to measure.
 * @returns {{x: number, y: number, width: number, height: number}|null} the rect.
 */
const getVisibleRect = (element) => {
  if (!element || isElementHidden(element)) {
    return null;
  }
  const {
    x, y, width, height,
  } = element.getBoundingClientRect();
  if (width === 0 || height === 0) {
    return null;
  }
  const rect = {
    x, y, width, height,
  };
  return isElementCovered(element, rect) ? null : rect;
};

/**
 * Builds the key entries for one piece of text rendered by an element.
 * @param {string} text - the text holding markers.
 * @param {Element} element - the element whose rect is reported.
 * @returns {Array<{key: string, rect: object}>} the entries.
 */
const buildKeyEntries = (text, element) => {
  const markers = decodeKeyMarkers(text);
  if (markers.length === 0) {
    return [];
  }
  const select = element.closest('select');
  // A collapsed native select paints its selected option in the select's box. The options
  // themselves have no rect; unopened alternatives are not screenshot evidence.
  const collapsedOption = element.tagName === 'OPTION' && select
    && !select.multiple && select.size <= 1;
  if (collapsedOption && !element.selected) {
    return [];
  }
  const rect = getVisibleRect(collapsedOption ? select : element);
  if (!rect) {
    return [];
  }
  return markers.map(({ key }) => ({ key, rect }));
};

// A placeholder is only on screen while its field is empty. A `title` attribute is not listed: the
// browser shows it as a tooltip on hover only, so a screenshot never contains it.
const PLACEHOLDER_SELECTOR = 'input[placeholder], textarea[placeholder]';

/**
 * Returns the i18n keys visible in the document body, with the rect of the element showing each.
 * Text keys use the parent element of the text node; placeholder keys use the empty field itself.
 * @param {Element} [root] - the element to scan.
 * @returns {Array<{key: string, rect: object}>} the visible keys, in document order.
 */
export const getVisibleKeys = (root = document.body) => {
  const entries = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    entries.push(...buildKeyEntries(node.nodeValue, node.parentElement));
  }
  root.querySelectorAll(PLACEHOLDER_SELECTOR).forEach((element) => {
    if (element.value === '') {
      entries.push(...buildKeyEntries(element.getAttribute('placeholder'), element));
    }
  });
  return entries;
};

/**
 * Installs `window.i18nCapture` for the capture runner.
 */
export const installCaptureApi = () => {
  window.i18nCapture = {
    version: typeof COMMITHASH !== 'undefined' ? COMMITHASH : undefined,
    getVisibleKeys,
  };
};
